import { Text, designTokens } from '@cinch-labs/design-system'
import cx from 'classnames'
import React from 'react'

import { Card } from '../card/card'
import Price from '../price/price'
import { ProductImage } from '../product-image/product-image'
import { SaveButton } from '../save-button/save-button'
import { Subtitle } from '../subtitle/subtitle'
import { Tags } from '../tags/tags'
import { Title } from '../title/title'
import styles from './product-list-card.module.css'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { AdminFeeText } from '../../../admin-fee-text-button/admin-fee-text.component'

interface TagItem {
  label: string
  value: string
}

export interface ProductListCardProps {
  id: string
  image?: string
  price: number
  title: string
  subtitle: string
  isAvailable?: boolean
  isSaved: boolean
  isPending: boolean
  tags: TagItem[]
  unavailable?: string
  isUnfavourited?: boolean
  isReserved: boolean
  showMonthlyPrice: boolean
  quoteRegularPaymentInPence?: number | null
  quoteType?: string | null
  make: string
  model: string
  discountInPence?: number
  vehicleYear?: string | number
  showActions?: boolean
  onSave?: (newVal: boolean) => void
  onCardClick?: () => void
  isSmallCardSize?: boolean
}

const filterTestTags = (tag: TagItem) => {
  if (tag.label) {
    return tag.label.match(/Mileage|Fuel Type|Transmission Type/)
  }

  return null
}

const hyphenate = (text: string | undefined) =>
  text ? text.replace(/\s+/g, '-').toLowerCase() : ''

const { font } = designTokens.customProperties.ds
const ERROR_MESSAGE = 'Image Unavailable'
const penceToPounds = (n: number): string => `${Math.round(n / 100).toFixed(0)}`

export const ProductListCard: React.FC<ProductListCardProps> = ({
  id,
  price,
  image,
  title,
  subtitle,
  isSaved,
  isPending = false,
  isAvailable,
  onSave,
  tags,
  unavailable,
  isReserved,
  showMonthlyPrice,
  quoteRegularPaymentInPence,
  quoteType,
  make,
  model,
  vehicleYear,
  discountInPence,
  showActions = true,
  isSmallCardSize = false,
  onCardClick,
}) => {
  const adminFeeValueInPounds = Number(readFromEnv(Env.AdminFeeValue))
  const hasAdminFeeComplianceBeenSet =
    readFromEnv(Env.AdminFeeCompliance) === 'true'

  const tagsCarCards = tags.filter((tag) => filterTestTags(tag))
  const vehicleTitle = `${vehicleYear} ${title}`
  const showOfferTag = !!discountInPence

  return (
    <Card data-testid="product-list-card" element="article" isHoverable>
      <div
        className={cx(styles.meta, styles.metaCarCards, {
          [styles.unavailable]: !isAvailable,
        })}
      >
        <div>
          <Title
            element="h2"
            className={cx(styles.title, styles.titleCarCards)}
            fontSizeFixed={'m'}
            type={'cardTitle'}
            isPending={isPending}
            aria-describedby={isReserved ? `vehicle-reserved-${id}` : undefined}
          >
            <a
              data-testid="product-list-card-link"
              href={`/used-cars/${hyphenate(make)}/${hyphenate(
                model,
              )}/details/${id}`}
              className={styles.link}
              onClick={() => (onCardClick ? onCardClick() : null)}
            >
              {vehicleTitle}
            </a>
          </Title>

          <Subtitle isPending={isPending} fontSize={'s'}>
            {subtitle}
          </Subtitle>

          <Tags
            isPending={isPending}
            listStyleClassName={styles.tagsCarCards}
            listItemClassName={styles.tagItemCarCard}
            items={tagsCarCards}
          />
        </div>

        {isAvailable && (
          <Price
            isPending={isPending}
            price={price}
            vehicleId={id}
            shouldShowCarsOnOfferTag={showOfferTag}
            discountInPence={discountInPence}
            showSmallFontSize={isSmallCardSize}
            showMonthlyPrice={showMonthlyPrice}
            quoteRegularPaymentInPence={quoteRegularPaymentInPence}
            quoteType={quoteType}
          />
        )}
        {!hasAdminFeeComplianceBeenSet && (
          <div className={styles.adminFeeButtonWrapper}>
            <AdminFeeText
              trackingValue={'cinchcharge featured car card'}
              adminFee={adminFeeValueInPounds}
            />
          </div>
        )}
      </div>

      <div className={styles.imageContainer}>
        <ProductImage
          alt={title}
          isAvailable={isAvailable}
          image={image}
          unavailableImageMessage={
            (!isAvailable && ' ') || unavailable || ERROR_MESSAGE
          }
          isPending={isPending}
        />

        {showActions && (
          <div className={styles.actions}>
            {!!onSave && isAvailable && (
              <SaveButton
                isPending={isPending || (!isAvailable && !isSaved)}
                className={styles.save}
                isSaved={isSaved}
                onClick={() => onSave(!isSaved)}
                size={font.size.xxl}
              />
            )}
          </div>
        )}

        {showOfferTag && !isReserved && (
          <Text
            fontSizeFixed="s"
            fontWeight="semibold"
            className={`${styles.badge} ${styles.discount}`}
            data-testid="vehicle-has-discount"
            id={`vehicle-discount-${id}`}
          >
            {`£${penceToPounds(discountInPence ?? 0)} off`}
          </Text>
        )}

        {isReserved && (
          <Text
            fontWeight="book"
            className={`${styles.badge} ${styles.reserved}`}
            data-testid="vehicle-is-reserved"
            id={`vehicle-reserved-${id}`}
          >
            Currently reserved
          </Text>
        )}
      </div>
    </Card>
  )
}
